'use client';
import BenefitsFilter from '@/components/BenefitsFilter';
import CardGrid, { CardData } from '@/components/CardGrid';
import GuideUtua from '@/components/GuideUtua';
import PageHeader from '@/components/PageHeader';
import CardIcon from '@/img/add_card.svg';
import MoneyBag from '@/img/money_bag.svg';

import MoneyDollar from '@/img/moneyDollar.svg';
import ShoppingCart from '@/img/shopping_cart.svg';
import { mapCardsData } from '@/mappers/FinancialInstitution';
import { Card } from '@/types/credit';
import { truncateText } from '@/utils/charactersLimit';
import { RequestErrorResponse } from '@/utils/request';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useState } from 'react';
import ErrorState from '../ErrorState';

interface CreditCardPageProps {
  cardsResponse: Card[] | RequestErrorResponse;
}

export default function CreditCardPageComponent({
  cardsResponse,
}: CreditCardPageProps) {
  const [cards, setCards] = useState<CardData[]>([]);
  const [error, setError] = useState<boolean>(false);
  const t = useTranslations('Index');

  useEffect(() => {
    if ('error' in cardsResponse) {
      setError(true);
    } else {
      const unifiedCardData = mapCardsData(cardsResponse);
      const sortedCardsData = unifiedCardData
        .sort(() => Math.random() - 0.5)
        .slice(0, 4);
      setCards(sortedCardsData);
    }
  }, [cardsResponse]);

  const renderCardGrid = () => {
    if (error) return <ErrorState />;

    return (
      <Suspense fallback={<>Loading</>}>
        <CardGrid
          title={t('cardGrid.creditCard.title')}
          cards={cards}
          pageType="CreditCardPage"
        />
      </Suspense>
    );
  };

  return (
    <>
      <PageHeader
        subtitle={t('pageHeader.creditCard.subtitle')}
        title={t('pageHeader.creditCard.title')}
        bgColor="dark"
      />
      <BenefitsFilter
        cards={[
          {
            image: MoneyDollar,
            text: truncateText(
              t.markup('creditCard.first', {
                strong: (text) => `<strong>${text}</strong>`,
              }),
              40
            ),
            linkToFilter: `/cartao-de-credito/todos`,
            queryParams: `benefit=${t('creditCard.first')}`,
          },
          {
            image: MoneyBag,
            text: truncateText(
              t.markup('creditCard.second', {
                strong: (text) => `<strong>${text}</strong>`,
              }),
              40
            ),
            linkToFilter: `/cartao-de-credito/todos`,
            queryParams: `benefit=${t('creditCard.second')}`,
          },
          {
            image: ShoppingCart,
            text: truncateText(
              t.markup('creditCard.third', {
                strong: (text) => `<strong>${text}</strong>`,
              }),
              30
            ),
            linkToFilter: `/cartao-de-credito/todos`,
            queryParams: `benefit=${t('creditCard.third')}`,
          },
        ]}
        title={t('benefitsFilter.creditCard.title')}
        textButton={truncateText(
          t.markup('benefitsFilter.creditCard.textButton', {
            strong: (text) => `<strong>${text}</strong>`,
          }),
          50
        )}
        cardFilterIcon={CardIcon}
        linkButtonAll="/cartao-de-credito/todos"
      />
      {renderCardGrid()}
      <GuideUtua
        title={t('guideUtua.title')}
        placeholder={t('guideUtua.placeholder')}
      />
    </>
  );
}
